import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  message,
  Radio,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  TimePicker,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import defaultMarkerIcon from 'routes/app/collections/mapMarkerIcon';
import {
  CollectionPoint,
  CollectionPointShift,
  useCancelCollection,
  useCollectionPoints,
  useCreateCollection,
} from 'services/CollectionsV2Service';
import { useAddCollection } from 'services/journeys';
import SITELINKS from 'constants/sitelinks';

const { Text } = Typography;

const PAGE_SIZE = 10;
const TIME_FORMAT = 'HH:mm';
const CUSTOM = 'custom';

const withTime = (date: Dayjs, time: string | null) => {
  if (!time) return date.hour(9).minute(0).second(0).millisecond(0);
  const [hour, minute] = time.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return date;
  return date.hour(hour).minute(minute).second(0).millisecond(0);
};

interface Props {
  open: boolean;
  journeyId: string;
  onClose: () => void;
}

const AddCollectionModal: React.FC<Props> = ({
  open,
  journeyId,
  onClose,
}: Props) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [chosen, setChosen] = useState<CollectionPoint | null>(null);
  const [shiftKey, setShiftKey] = useState<number | typeof CUSTOM>(CUSTOM);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [time, setTime] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(20);

  const points = useCollectionPoints(
    { active: true, q: search || undefined, page, pageSize: PAGE_SIZE },
    open,
  );

  const { mutateAsync: createCollection, isPending: isCreating } =
    useCreateCollection();
  const { mutateAsync: addCollection, isPending: isAdding } =
    useAddCollection();
  const { mutateAsync: cancelCollection } = useCancelCollection();

  const busy = isCreating || isAdding;

  const reset = () => {
    setChosen(null);
    setShiftKey(CUSTOM);
    setTime(null);
    setDuration(20);
  };

  const close = () => {
    reset();
    onClose();
  };

  const applyShift = (shift: CollectionPointShift) => {
    setShiftKey(shift.id);
    setTime(shift.startTime);
    setDuration(shift.estimatedDurationMinutes);
  };

  const pickPoint = (point: CollectionPoint) => {
    setChosen(point);
    setDate(dayjs());
    const shifts = point.shifts ?? [];
    if (shifts.length > 0) applyShift(shifts[0]);
    else {
      setShiftKey(CUSTOM);
      setTime(null);
      setDuration(20);
    }
  };

  const confirm = async () => {
    if (!chosen) return;
    const scheduledStart = withTime(date, time).toISOString();
    let created: { id: number } | null = null;
    try {
      created = await createCollection({
        collectionPointId: chosen.id,
        journeyId,
        scheduledStart,
        estimatedDurationMinutes: duration,
      });
      await addCollection({ journeyId, collectionId: created.id });
      close();
    } catch (error) {
      if (created) {
        cancelCollection(created.id).catch(() => {});
      }
      const failed = error as { name?: string; message?: string };
      if (failed?.name !== 'JourneyError') {
        message.error(failed?.message ?? 'No se pudo crear la colecta');
      }
      setChosen(null);
    }
  };

  const pointColumns: TableColumnsType<CollectionPoint> = [
    { title: '#', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Tienda',
      key: 'store',
      render: (_v, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.storeName}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.marketplaceName}
          </Text>
        </Space>
      ),
    },
    { title: 'Dirección', dataIndex: 'address', key: 'address' },
    {
      title: 'Turnos',
      dataIndex: 'shifts',
      key: 'shifts',
      width: 220,
      render: (shifts: CollectionPointShift[]) =>
        shifts?.length ? (
          <Space size={4} wrap>
            {shifts.map((shift) => (
              <Tag key={shift.id} style={{ margin: 0 }}>
                {shift.startTime} · {shift.estimatedDurationMinutes} min
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">Sin turnos</Text>
        ),
    },
    {
      key: 'add',
      width: 110,
      render: (_v, record) => (
        <Button type="primary" size="small" onClick={() => pickPoint(record)}>
          Agregar
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={close}
      width={900}
      destroyOnClose
      title={`Agregar colecta a la ruta ${journeyId}`}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => window.open(SITELINKS.collections.points, '_blank')}
          >
            Dar de alta un punto de colecta
          </Button>
          <Space>
            {chosen && (
              <Button onClick={reset} disabled={busy}>
                Elegir otra tienda
              </Button>
            )}
            <Button onClick={close} disabled={busy}>
              Cancelar
            </Button>
            <Button
              type="primary"
              disabled={!chosen}
              loading={busy}
              onClick={confirm}
            >
              Agregar a la ruta
            </Button>
          </Space>
        </div>
      }
    >
      {!chosen && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Elegí la tienda</Text>
          <Input.Search
            placeholder="Buscar por tienda, dirección o marketplace"
            allowClear
            onSearch={(value) => {
              setPage(0);
              setSearch(value);
            }}
          />
          <Table
            rowKey="id"
            size="small"
            columns={pointColumns}
            dataSource={points.data?.items ?? []}
            loading={points.isFetching}
            pagination={{
              current: page + 1,
              pageSize: PAGE_SIZE,
              total: points.data?.totalElements ?? 0,
              showSizeChanger: false,
              onChange: (next) => setPage(next - 1),
            }}
          />
        </Space>
      )}

      {chosen && (
        <Spin spinning={busy}>
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Card size="small">
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 16 }}>
                      {chosen.storeName}
                    </Text>
                    <Text type="secondary">{chosen.marketplaceName}</Text>
                    <Text type="secondary">{chosen.address}</Text>
                  </Space>
                </Col>
                <Col xs={24} md={12}>
                  <div style={{ height: 160 }}>
                    <MapContainer
                      key={`${chosen.lat},${chosen.lng}`}
                      center={[chosen.lat, chosen.lng]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker
                        position={[chosen.lat, chosen.lng]}
                        icon={defaultMarkerIcon}
                      />
                    </MapContainer>
                  </div>
                </Col>
              </Row>
            </Card>

            <div>
              <Text strong>¿Cuándo pasa el driver?</Text>
              <Text type="secondary" style={{ display: 'block', marginTop: 2 }}>
                Cada turno indica la hora de llegada y el tiempo de permanencia
                del driver en tienda.
              </Text>
              <div style={{ marginTop: 8 }}>
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  value={shiftKey}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (value === CUSTOM) {
                      setShiftKey(CUSTOM);
                      return;
                    }
                    const shift = (chosen.shifts ?? []).find(
                      (s) => s.id === value,
                    );
                    if (shift) applyShift(shift);
                  }}
                >
                  {(chosen.shifts ?? []).map((shift) => (
                    <Radio.Button key={shift.id} value={shift.id}>
                      {shift.startTime} · {shift.estimatedDurationMinutes} min
                    </Radio.Button>
                  ))}
                  <Radio.Button value={CUSTOM}>Otro horario</Radio.Button>
                </Radio.Group>
              </div>
              {(chosen.shifts ?? []).length === 0 && (
                <Text
                  type="secondary"
                  style={{ display: 'block', marginTop: 8 }}
                >
                  Este punto no tiene turnos cargados: cargá acá la hora de
                  llegada y el tiempo en la tienda.
                </Text>
              )}
            </div>

            <Space size={16} align="start" wrap>
              <Space direction="vertical" size={4}>
                <Text>Fecha</Text>
                <DatePicker
                  value={date}
                  format="DD/MM/YYYY"
                  allowClear={false}
                  disabledDate={(day) => day.isBefore(dayjs(), 'day')}
                  onChange={(value) => value && setDate(value)}
                />
              </Space>
              {shiftKey === CUSTOM ? (
                <>
                  <Space direction="vertical" size={4}>
                    <Text>Hora de llegada a la tienda</Text>
                    <TimePicker
                      popupClassName="hora-turno"
                      value={withTime(date, time)}
                      format={TIME_FORMAT}
                      minuteStep={5}
                      allowClear={false}
                      onChange={(value) =>
                        value && setTime(value.format(TIME_FORMAT))
                      }
                    />
                  </Space>
                  <Space direction="vertical" size={4}>
                    <Text>Tiempo en la tienda</Text>
                    <InputNumber
                      min={1}
                      max={240}
                      addonAfter="min"
                      value={duration}
                      onChange={(value) => setDuration(value ?? 1)}
                    />
                  </Space>
                </>
              ) : (
                <Space direction="vertical" size={4}>
                  <Text>Turno elegido</Text>
                  <Text style={{ fontSize: 16, lineHeight: '32px' }}>
                    Llega {time} · se queda {duration} min (hasta las{' '}
                    {withTime(date, time)
                      .add(duration, 'minute')
                      .format(TIME_FORMAT)}
                    )
                  </Text>
                </Space>
              )}
            </Space>
          </Space>
        </Spin>
      )}
    </Modal>
  );
};

export default AddCollectionModal;
